import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Search, Package, MapPin, Clock, CheckCircle2, Truck, Home, Sparkles, Radio, Timer, Navigation, RotateCw } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import LiveMap from "../components/LiveMap";
import BookingQRCard from "../components/BookingQRCard";
import ReferralCard from "../components/ReferralCard";

const STEPS = [
  { key: "new",        label: "Booking modtaget", icon: Sparkles,   fld: "created_at"     },
  { key: "confirmed",  label: "Bekræftet",        icon: CheckCircle2, fld: null           },
  { key: "in_transit", label: "Afhentet — undervejs", icon: Truck,   fld: "picked_up_at"  },
  { key: "delivered",  label: "Leveret",          icon: Home,        fld: "delivered_at"  },
];

const order = ["new", "confirmed", "in_transit", "delivered"];
const formatDk = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
};

export default function TrackPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = React.useState({
    booking_number: params.get("b") || "",
    email: params.get("e") || "",
  });
  const [booking, setBooking] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);

  const fetchTrack = async (data) => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/track", {
        booking_number: data.booking_number.trim().toUpperCase(),
        email: data.email.trim().toLowerCase(),
      });
      setBooking(res);
      setSearched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kunne ikke hente sporing";
      setBooking(null);
      setSearched(true);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.booking_number || !form.email) {
      toast.error("Udfyld booking-nummer og email"); return;
    }
    setParams({ b: form.booking_number.trim().toUpperCase(), e: form.email.trim().toLowerCase() });
    await fetchTrack(form);
  };

  // Auto-fetch if URL has params
  React.useEffect(() => {
    if (params.get("b") && params.get("e") && !searched) {
      fetchTrack({ booking_number: params.get("b"), email: params.get("e") });
    }
    // eslint-disable-next-line
  }, []);

  // Auto refresh every 30s if booking is active
  React.useEffect(() => {
    if (!booking || ["delivered", "cancelled"].includes(booking.booking_status)) return;
    const t = setInterval(() => { fetchTrack(form); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [booking?.booking_status]);

  // Live driver location polling — only when in_transit
  const [liveLoc, setLiveLoc] = React.useState(null);
  const [eta, setEta] = React.useState(null);
  React.useEffect(() => {
    if (!booking || booking.booking_status !== "in_transit") { setLiveLoc(null); setEta(null); return; }
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [locRes, etaRes] = await Promise.allSettled([
          api.post("/track/live", {
            booking_number: booking.booking_number,
            email: form.email.trim().toLowerCase(),
          }),
          api.post("/track/eta", {
            booking_number: booking.booking_number,
            email: form.email.trim().toLowerCase(),
          }),
        ]);
        if (cancelled) return;
        if (locRes.status === "fulfilled") setLiveLoc(locRes.value.data);
        if (etaRes.status === "fulfilled") setEta(etaRes.value.data);
      } catch (e) { console.error("Live tracking poll failed:", e); }
    };
    fetchLive();
    const t = setInterval(fetchLive, 20000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line
  }, [booking?.booking_status, booking?.booking_number]);

  const currentIdx = booking ? order.indexOf(booking.booking_status === "cancelled" ? "new" : booking.booking_status) : -1;
  const cancelled = booking?.booking_status === "cancelled";

  return (
    <div data-testid="track-page">
      {form.email && booking && (
        <button
          type="button"
          onClick={() => {
            const card = document.querySelector('[data-testid="referral-card"]');
            if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
          className="w-full bg-brand-red text-white text-center text-sm py-2.5 px-4 font-semibold hover:bg-brand-red/90 transition-colors"
          data-testid="referral-share-banner"
        >
          📨 Din anbefalingskode er klar — del og spar 50 kr pr. ven →
        </button>
      )}
      <section className="bg-brand-dark text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Live tracking</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[1.05]">
            {t("track.title")}.
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl leading-relaxed">
            {t("track.subtitle")}.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
        <form onSubmit={submit} className="bg-white border border-brand p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-4 items-end" data-testid="track-form">
          <div className="sm:col-span-5">
            <label className="label-flat">{t("track.booking_number")}</label>
            <input
              className="input-flat font-mono"
              placeholder="EMA-251214-AB12CD"
              value={form.booking_number}
              onChange={(e) => setForm((f) => ({ ...f, booking_number: e.target.value }))}
              data-testid="track-input-number"
              required
            />
          </div>
          <div className="sm:col-span-5">
            <label className="label-flat">{t("track.email")}</label>
            <input
              type="email"
              className="input-flat"
              placeholder="din@email.dk"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="track-input-email"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="track-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <><Search size={16} /> {t("track.track_btn")}</>}
            </button>
          </div>
        </form>

        {/* Result */}
        {booking && (
          <div className="mt-8" data-testid="track-result">
            {/* Header card */}
            <div className="bg-brand-dark text-white p-7 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="kicker text-brand-red">Booking</div>
                <div className="font-mono font-bold text-2xl mt-2">{booking.booking_number}</div>
                <div className="text-white/60 text-sm mt-1">Hej {booking.customer_name}</div>
              </div>
              <div>
                <div className="kicker text-brand-red">Rute</div>
                <div className="mt-2 text-sm">
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-brand-red" /> {booking.pickup_zip} {booking.pickup_city}</div>
                  <div className="text-white/40 text-xs my-1 ml-1">↓</div>
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-brand-red" /> {booking.delivery_zip} {booking.delivery_city}</div>
                </div>
              </div>
              <div>
                <div className="kicker text-brand-red">Detaljer</div>
                <div className="text-sm mt-2 grid grid-cols-2 gap-2">
                  <div><div className="text-white/50 text-[10px] uppercase tracking-widest">Tid</div><div className="font-semibold">{booking.pickup_time}</div></div>
                  <div><div className="text-white/50 text-[10px] uppercase tracking-widest">Pakker</div><div className="font-semibold">{booking.package_count}</div></div>
                  <div><div className="text-white/50 text-[10px] uppercase tracking-widest">Vægt</div><div className="font-semibold">{booking.weight_kg} kg</div></div>
                  <div><div className="text-white/50 text-[10px] uppercase tracking-widest">Beløb</div><div className="font-semibold">{booking.total_amount_dkk} kr</div></div>
                </div>
              </div>
            </div>

            {/* QR code for the driver to scan at pickup (only after booking is paid/confirmed and not delivered) */}
            {booking.qr_token && booking.payment_status === "paid" && booking.booking_status !== "delivered" && (
              <div className="mt-6">
                <BookingQRCard
                  qrToken={booking.qr_token}
                  bookingNumber={booking.booking_number}
                  status={booking.booking_status}
                />
              </div>
            )}

            {/* Referral card — always shown when we have an email */}
            {form.email && (
              <div className="mt-6">
                <ReferralCard customerEmail={form.email.trim().toLowerCase()} />
              </div>
            )}

            {/* Genbestil — quick re-order CTA (always available once we have a booking) */}
            {form.email && (
              <a
                href={`/book?reorder=${encodeURIComponent(booking.booking_number)}&e=${encodeURIComponent(form.email.trim().toLowerCase())}`}
                className="mt-6 bg-white border border-brand p-5 sm:p-6 flex items-center justify-between gap-4 hover:bg-brand-bg2 transition group"
                data-testid="track-cta-reorder"
              >
                <div>
                  <div className="kicker">Genbestil samme rute</div>
                  <div className="font-display font-semibold text-brand-dark text-lg mt-1">Endnu en levering · {booking.pickup_city} → {booking.delivery_city}</div>
                  <div className="text-brand-text2 text-sm mt-1">Vi udfylder formularen for dig — ret blot dato og betal.</div>
                </div>
                <div className="bg-brand-red text-white w-12 h-12 flex items-center justify-center group-hover:scale-105 transition flex-shrink-0">
                  <RotateCw size={18} />
                </div>
              </a>
            )}

            {/* Live driver map + ETA (only during in_transit) */}
            {booking.booking_status === "in_transit" && (
              <div className="bg-white border border-brand border-t-0" data-testid="live-driver-map">
                {/* ETA Banner */}
                {eta?.available && (
                  <div className="bg-gradient-to-r from-brand-dark to-brand-dark/90 text-white px-7 sm:px-8 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="eta-banner">
                    <div>
                      <div className="kicker text-brand-red flex items-center gap-1.5">
                        <Timer size={11} /> Estimeret ankomst
                      </div>
                      <div className="font-display font-bold text-3xl sm:text-4xl mt-1">
                        {eta.duration_min < 1 ? "<1 min" : `${eta.duration_min} min`}
                      </div>
                      <div className="text-white/60 text-xs mt-1">
                        Kommer kl. {new Date(eta.eta_at).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div>
                      <div className="kicker text-brand-red flex items-center gap-1.5">
                        <Navigation size={11} /> Afstand
                      </div>
                      <div className="font-display font-bold text-3xl sm:text-4xl mt-1">
                        {eta.distance_km < 1 ? `${eta.distance_m} m` : `${eta.distance_km} km`}
                      </div>
                      <div className="text-white/60 text-xs mt-1">Live via OSM rute</div>
                    </div>
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                        <span className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Live opdatering</span>
                      </div>
                      <div className="text-white/70 text-xs mt-2 leading-relaxed">
                        Opdateres automatisk hver 20. sekund baseret på chaufførens position.
                      </div>
                    </div>
                  </div>
                )}
                <div className="px-7 sm:px-8 py-4 border-b border-brand flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Radio size={14} className="text-emerald-600 animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-widest text-brand-dark">Live chauffør-position</span>
                  </div>
                  {liveLoc?.available && (
                    <span className="text-xs text-brand-text2 font-mono">
                      Opdateret {new Date(liveLoc.at).toLocaleTimeString("da-DK")}
                    </span>
                  )}
                </div>
                {liveLoc?.available ? (
                  <LiveMap
                    drivers={[{
                      id: "live",
                      name: "Din chauffør",
                      location: { lat: liveLoc.lat, lng: liveLoc.lng, at: liveLoc.at, accuracy_m: liveLoc.accuracy_m },
                      is_online: true,
                      active_bookings: [],
                    }]}
                    packages={eta?.available ? [{ id: "dest", lat: eta.destination_lat, lng: eta.destination_lng, label: "Leveringsadresse" }] : []}
                    height={360}
                    zoom={13}
                  />
                ) : (
                  <div className="p-7 text-center text-sm text-brand-text2">
                    <Radio size={28} className="text-brand-text2/40 mx-auto mb-3" />
                    {liveLoc?.reason || "Afventer GPS-signal..."}
                  </div>
                )}
              </div>
            )}

            {/* Timeline */}
            <div className="bg-white border border-brand border-t-0 p-7 sm:p-10">
              {cancelled ? (
                <div className="bg-red-50 border border-red-200 text-red-900 p-5 text-sm" data-testid="track-cancelled">
                  Denne booking er annulleret. Kontakt <a className="font-semibold underline" href="mailto:kontakt@emakurer.dk">kontakt@emakurer.dk</a> for spørgsmål.
                </div>
              ) : (
                <ol className="relative" data-testid="track-timeline">
                  <div className="absolute left-5 top-3 bottom-3 w-px bg-brand z-0" />
                  {STEPS.map((s, i) => {
                    const reached = i <= currentIdx;
                    const ts = s.fld ? booking[s.fld] : (s.key === "confirmed" && booking.payment_status === "paid" ? (booking.timeline?.find(t => t.status === "confirmed")?.at || null) : null);
                    return (
                      <li key={s.key} className="relative pl-16 pb-8 last:pb-0" data-testid={`timeline-${s.key}`}>
                        <div className={`absolute left-0 top-0 w-11 h-11 flex items-center justify-center z-10 ${reached ? "bg-brand-red text-white" : "bg-white border border-brand text-brand-text2"}`}>
                          <s.icon size={18} />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                          <div>
                            <div className={`font-display font-semibold text-lg ${reached ? "text-brand-dark" : "text-brand-text2"}`}>{s.label}</div>
                            {reached && i === currentIdx && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red mt-1 uppercase tracking-widest">
                                <span className="w-1.5 h-1.5 bg-brand-red rounded-full animate-pulse" /> Aktuel status
                              </span>
                            )}
                          </div>
                          <div className={`text-xs font-mono ${reached ? "text-brand-dark" : "text-brand-text2/50"}`}>
                            {formatDk(ts) || (reached ? "—" : "Afventer")}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}

              {/* Event log */}
              {booking.timeline && booking.timeline.length > 0 && !cancelled && (
                <div className="border-t border-brand mt-7 pt-7">
                  <div className="kicker">Hændelseslog</div>
                  <ul className="mt-3 space-y-2 text-sm text-brand-text2">
                    {[...booking.timeline].reverse().map((t, i) => (
                      <li key={`${t.at}-${t.status}-${i}`} className="flex items-start gap-3" data-testid={`log-${i}`}>
                        <Clock size={14} className="text-brand-red mt-0.5 flex-shrink-0" />
                        <span className="font-mono text-xs text-brand-dark min-w-[110px]">{formatDk(t.at)}</span>
                        <span>{t.note || t.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="text-xs text-brand-text2 mt-5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Auto-opdaterer hvert 30. sekund
            </div>
          </div>
        )}

        {searched && !booking && !loading && (
          <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-900 p-6" data-testid="track-not-found">
            <div className="font-display font-semibold text-lg">Ingen booking fundet</div>
            <div className="text-sm mt-1">Tjek at booking-nummer og email matcher det, du brugte ved booking. Brug for hjælp? Skriv til <a href="mailto:kontakt@emakurer.dk" className="font-semibold underline">kontakt@emakurer.dk</a>.</div>
          </div>
        )}

        {!searched && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {[
              { i: Package, t: "Find dit booking-nummer", d: "Booking-nummeret står i bekræftelsen, du så efter betaling — fx EMA-251214-AB12CD" },
              { i: Search, t: "Realtidsstatus", d: "Hver gang vi opdaterer status, ser du det med det samme her" },
              { i: ArrowRight, t: "Direkte link", d: "Gem siden som bogmærke — der er ikke noget login nødvendigt" },
            ].map((c) => (
              <div key={c.t} className="card-flat p-6">
                <c.i className="text-brand-red" size={18} />
                <div className="font-display font-semibold text-brand-dark mt-3">{c.t}</div>
                <div className="text-brand-text2 mt-1">{c.d}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
